import { Chip, Grid, Link, Tooltip, IconButton, CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { fetchOrderDetails } from '../../../../../stores/redux/actions';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import InfoIcon from '@mui/icons-material/Info';
import { OrderCommand } from '../../../../../services/command';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import SyncIcon from '@mui/icons-material/Sync';
import Swal from 'sweetalert2';
import UserNotDeliveredReasons from './UserNotDeliveredReasons';
import { DeliveryMethodEnum } from '../../../../../types/enums';
import { UOAH, uopdCls } from '../userOrderProductDetails.classes';
import { getCargoAgreementLabel, getDeliveryTypeIcon } from './UserOrderAccordionHeader.helpers';
import { AccordionPhotoPreview } from './UserOrderAccordionHeaderPhotos';

function UserOrderAccordionHeader({
    shipment,
    orderId,
    customerId,
    isExpanded,
    handleOpenModal,
    isOutofStockHeader,
    reshippingActionButtonAllowed,
}: any) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [storeCode, setStoreCode] = React.useState<any>('');
    const [copied, setCopied] = useState<any>({});
    const [underlinedElement, setUnderlinedElement] = useState(null);
    const timeoutRefs = useRef<any>({});
    const [reShipLoading, setReShipLoading] = useState(false);

    const isAnimationRunning = false;

    const handleMouseEnter = (elementId: any) => {
        setUnderlinedElement(elementId);
    };

    const handleMouseLeave = () => {
        setUnderlinedElement(null);
    };

    useEffect(() => {
        if (shipment?.shipmentType == 'MP-Mağazadan Dene Al') {
            getStoreCodeFromAddresApi();
        }
    }, []);

    useEffect(() => {
        return () => {
            Object.values(timeoutRefs.current).forEach((timeoutId: any) => {
                if (timeoutId) clearTimeout(timeoutId);
            });
        };
    }, []);

    const getStoreCodeFromAddresApi = () => {
        OrderCommand.GetSaleOrderShippingBillingAddress({ orderId }).then((res) => {
            if (Number(res?.saleOrderShippingAddressModel?.deliveryMethodId) !== DeliveryMethodEnum.PickPoint && res?.saleOrderShippingAddressModel?.externalReferanceCode)
                setStoreCode(res?.saleOrderShippingAddressModel?.externalReferanceCode);
        });
    };

    const copyToClipboard = (e: any, text: any, field: any) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);

        if (timeoutRefs.current[field]) {
            clearTimeout(timeoutRefs.current[field]);
        }

        setCopied((prev: any) => ({ ...prev, [field]: true }));

        timeoutRefs.current[field] = setTimeout(() => {
            setCopied((prev: any) => ({ ...prev, [field]: false }));
            delete timeoutRefs.current[field];
        }, 1500);
    };

    const handleReShipConfirm = async () => {
        setReShipLoading(true);
        try {
            await OrderCommand.ReshipShipment({
                customerId,
                shippingListId: shipment.shippingListId,
                shippingListNo: shipment.shippingListNo,
                description: 'Backoffice ReShipping',
            });

            setReShipLoading(false);

            await Swal.fire({
                text: t('orderDetail.reShipSuccess'),
                icon: 'success',
                confirmButtonText: t('general.ok'),
                confirmButtonColor: '#3085d6',
                timer: 2500,
                timerProgressBar: true,
            });

            dispatch(fetchOrderDetails({ orderId: orderId, cultureCode: 'tr-TR' }));
        } catch (error) {
            console.error('Re-ship error:', error);
            setReShipLoading(false);
            await Swal.fire({
                text: t('orderDetail.reShipError'),
                icon: 'error',
                confirmButtonText: t('general.ok'),
                confirmButtonColor: '#3085d6',
            });
        } finally {
            setReShipLoading(false);
        }
    };

    const handleReShipClick = async (e: any) => {
        e.stopPropagation();
        const result = await Swal.fire({
            text: t('orderDetail.reShipConfirmMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.cancel'),
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
        });

        if (result.isConfirmed) {
            await handleReShipConfirm();
        }
    };

    const chipStyleBackground = {
        padding: '0.7rem 1.5rem',
        backgroundColor: '#e0e0e0',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '0.5rem',
        cursor: 'pointer',
        marginTop: '0.80rem',
    };

    return (
        <Grid
            className={UOAH.root}
            container
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ marginBottom: '1rem', marginTop: '1rem', position: 'relative', width: '100%' }}
        >
            <Grid
                className={UOAH.shipmentsGrid}
                style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: '2rem',
                    flexWrap: 'wrap',
                }}
                size={{
                    xs: 10.5,
                }}
            >
                {shipment.shippingListId && (
                    <div
                        className={UOAH.shippingIdWrap}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {copied.shippingListId && (
                            <div
                                className={UOAH.shippingIdCopiedBadge}
                                style={{
                                    color: 'green',
                                    fontStyle: 'italic',
                                    fontSize: '10px',
                                    position: 'absolute',
                                    marginTop: '-10px',
                                    marginLeft: '2rem',
                                }}
                            >
                                {t('orderDetail.copied')}
                            </div>
                        )}
                        <Tooltip title={t('orderDetail.shipmentId')}>
                            <Link
                                className={UOAH.shippingIdLink}
                                onClick={(e) => copyToClipboard(e, shipment.shippingListId, 'shippingListId')}
                                style={{ textDecoration: 'none', color: 'black' }}
                            >
                                <div>
                                    <div
                                        onMouseEnter={() => handleMouseEnter('icon')}
                                        onMouseLeave={() => handleMouseLeave}
                                        style={{ display: 'flex' }}
                                    >
                                        <strong
                                            style={{
                                                textDecoration: underlinedElement === 'icon' ? 'underline' : 'none',
                                                marginTop: 10,
                                            }}
                                            className="text-md"
                                        >
                                            {' '}
                                            #
                                        </strong>
                                    </div>
                                </div>
                            </Link>
                        </Tooltip>
                    </div>
                )}
                {shipment.shippingListNo && (
                    <div
                        className={UOAH.shipmentNoWrap}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {copied.shippingListNo && (
                            <div
                                className={UOAH.shipmentNoCopiedBadge}
                                style={{
                                    color: 'green',
                                    fontStyle: 'italic',
                                    fontSize: '10px',
                                    position: 'absolute',
                                    marginTop: '-10px',
                                }}
                            >
                                {t('orderDetail.copied')}
                            </div>
                        )}
                        <Tooltip title={t('orderDetail.shipmentNo')}>
                            <Link
                                className={UOAH.shipmentNoLink}
                                onClick={(e) => copyToClipboard(e, shipment.shippingListNo, 'shippingListNo')}
                                style={{ textDecoration: 'none', color: 'black' }}
                            >
                                <div style={{ display: 'flex' }}>
                                    <strong
                                        style={{
                                            marginTop: '1.2rem',
                                            marginRight: '1rem',
                                            marginLeft: '-1.5rem',
                                        }}
                                        className="text-md"
                                    >
                                        {t('orderDetail.shipment')}
                                    </strong>
                                    <div
                                        style={{
                                            ...chipStyleBackground,
                                            backgroundColor: '#CFE2F3',
                                        }}
                                    >
                                        <div
                                            onMouseEnter={() => handleMouseEnter('text')}
                                            onMouseLeave={() => handleMouseLeave}
                                            style={{ display: 'flex' }}
                                        >
                                            <p
                                                style={{
                                                    textDecoration: underlinedElement === 'text' ? 'underline' : 'none',
                                                    margin: 0,
                                                    marginLeft: '5px',
                                                }}
                                                className="text-sm"
                                            >
                                                {' '}
                                                {shipment.shippingListNo}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </Tooltip>
                    </div>
                )}
                {shipment.warehouseName && (
                    <div
                        className={UOAH.warehouseNameWrap}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {copied.warehouseName && (
                            <div
                                className={UOAH.warehouseNameCopiedBadge}
                                style={{
                                    color: 'green',
                                    fontStyle: 'italic',
                                    fontSize: '10px',
                                    position: 'absolute',
                                    marginTop: '-10px',
                                }}
                            >
                                {t('orderDetail.copied')}
                            </div>
                        )}
                        <Tooltip title={t('orderDetail.warehouseName')}>
                            <Link
                                className={UOAH.warehouseNameLink}
                                onClick={(e) => copyToClipboard(e, shipment.warehouseName, 'warehouseName')}
                                style={{ textDecoration: 'none', color: 'black' }}
                            >
                                <div style={chipStyleBackground}>
                                    <div
                                        onMouseEnter={() => handleMouseEnter('warehouse')}
                                        onMouseLeave={() => handleMouseLeave}
                                        style={{ display: 'flex' }}
                                    >
                                        <p
                                            style={{
                                                textDecoration:
                                                    underlinedElement === 'warehouse' ? 'underline' : 'none',
                                                margin: 0,
                                                textAlign: 'center',
                                            }}
                                            className="text-sm"
                                        >
                                            {shipment.warehouseName}
                                        </p>
                                    </div>
                                </div>
                            </Link>
                        </Tooltip>
                    </div>
                )}

                {shipment.shipmentType && shipment.shipmentType != 'Depo' && (
                    <Tooltip title={t('orderDetail.shipmentType')}>
                        <Chip
                            icon={<LocalMallIcon />}
                            className={UOAH.shipmentTypeChip}
                            style={{ marginLeft: '1rem', marginTop: '0.5rem' }}
                            label={shipment.shipmentType + (storeCode && ' (' + storeCode + ')')}
                            color="default"
                        />
                    </Tooltip>
                )}

                {shipment.shippingCompanyName && (
                    <Tooltip title={t('orderDetail.cargoTracking')}>
                        <Link
                            className={UOAH.cargoTrackingLink}
                            href={shipment.trackingNumber}
                            target="blank"
                            onClick={(e: any) => {
                                e.stopPropagation();
                            }}
                        >
                            <Chip
                                icon={<LocalShippingIcon />}
                                className={UOAH.cargoTrackingChip}
                                style={{
                                    marginLeft: '1rem',
                                    marginTop: '0.5rem',
                                    color: '#03a9f4',
                                    textDecoration: 'underline',
                                }}
                                label={shipment.shippingCompanyName}
                                color="default"
                            />
                        </Link>
                    </Tooltip>
                )}
                {shipment.shippingCompanyId > 0 && shipment.isSellerStore && (
                    <Tooltip title={t('orderDetail.cargoAgreement')}>
                        <Chip
                            icon={<LocalMallIcon />}
                            className={UOAH.cargoAgreementChip}
                            style={{ marginLeft: '1rem', marginTop: '0.5rem', background: '#9ade23', color: 'white' }}
                            label={getCargoAgreementLabel(shipment.shippingCompanyId)}
                        />
                    </Tooltip>
                )}
                {!isOutofStockHeader && shipment.shippingListStatusDecription && (
                    <Tooltip title={t('orderDetail.shipmentStatus')}>
                        <Link
                            className={UOAH.shipmentStatusLink}
                            onClick={(e: any) => handleOpenModal(e, shipment.shippingListId)}
                        >
                            <Chip
                                icon={<InfoIcon />}
                                className={UOAH.shipmentStatusChip}
                                style={{ marginLeft: '1rem', marginTop: '0.5rem' }}
                                label={shipment.shippingListStatusDecription}
                                color={shipment.shippingListStatusDecription == 'Teslim Edildi' ? 'success' : 'info'}
                            />
                        </Link>
                    </Tooltip>
                )}
                {!isOutofStockHeader && shipment.isReShipable && reshippingActionButtonAllowed && (
                    <Tooltip title={t('orderDetail.reShipAction')}>
                        <span
                            className={UOAH.reShipActionWrap}
                            style={{ marginLeft: '-0.5rem', marginTop: '0.5rem', display: 'inline-flex' }}
                        >
                            <IconButton
                                className={UOAH.reShipIconButton}
                                size="small"
                                disabled={reShipLoading}
                                onClick={(e) => handleReShipClick(e)}
                                aria-label={t('orderDetail.reShipAction')}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    padding: 0,
                                    bgcolor: '#f5f5f5',
                                    border: '1px solid #e8e8e8',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                    '&:hover': { bgcolor: '#eeeeee', borderColor: '#e0e0e0' },
                                    '&.Mui-disabled': { bgcolor: '#f5f5f5', borderColor: '#e8e8e8' },
                                }}
                            >
                                {reShipLoading ? (
                                    <CircularProgress
                                        className={UOAH.reShipLoading}
                                        size={18}
                                        sx={{ color: 'action.active' }}
                                    />
                                ) : (
                                    <SyncIcon sx={{ fontSize: 20, color: 'action.active' }} />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {shipment.isDPackage && shipment.cargoShipmentCode && (
                    <div
                        className={uopdCls(UOAH.mergedShipmentCode)}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {copied.cargoShipmentCode && (
                            <div
                                style={{
                                    color: 'green',
                                    fontStyle: 'italic',
                                    fontSize: '10px',
                                    position: 'absolute',
                                    marginTop: '-10px',
                                }}
                            >
                                {t('orderDetail.copied')}
                            </div>
                        )}
                        <Tooltip title={t('orderDetail.sharedShipmentCode')}>
                            <div
                                className={UOAH.sharedShipmentCodeClickWrap}
                                onClick={(e) => copyToClipboard(e, shipment.cargoShipmentCode, 'cargoShipmentCode')}
                                style={{
                                    textDecoration: 'none',
                                    color: 'black',
                                    cursor: 'pointer',
                                }}
                            >
                                <div
                                    className={UOAH.sharedShipmentCodeClickArea}
                                    style={{
                                        ...chipStyleBackground,
                                        backgroundColor: '#FFF9C4',
                                    }}
                                >
                                    <div
                                        onMouseEnter={() => handleMouseEnter('cargoShipmentCode')}
                                        onMouseLeave={() => handleMouseLeave}
                                        style={{ display: 'flex' }}
                                    >
                                        <p
                                            style={{
                                                textDecoration:
                                                    underlinedElement === 'cargoShipmentCode' ? 'underline' : 'none',
                                                margin: 0,
                                                marginLeft: '5px',
                                            }}
                                            className="text-sm"
                                        >
                                            {t('orderDetail.sharedShipmentCode')}: {shipment.cargoShipmentCode}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Tooltip>
                    </div>
                )}

                {shipment.consolidatedShippingListNo && (
                    <Link
                        className={UOAH.consolidatedOrderLink}
                        style={{
                            marginLeft: '1rem',
                            marginTop: '1rem',
                            color: '#007bff',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        target="_blank"
                        href={'/orderdetail?orderId=' + shipment.consolidatedOrderId}
                    >
                        <MergeTypeIcon style={{ marginRight: '0.5rem' }} />
                        {t('orderDetail.consolidatedOrder')}
                    </Link>
                )}
                {shipment.deliveryType && (
                    <Tooltip title={t('orderDetail.deliveryType')}>
                        <Chip
                            className={UOAH.deliveryTypeChip}
                            icon={getDeliveryTypeIcon(shipment.deliveryTypeId) as any}
                            style={{ marginLeft: '-0.5rem', marginTop: '0.5rem' }}
                            label={shipment.deliveryType}
                            color={'warning'}
                        />
                    </Tooltip>
                )}
                {!isOutofStockHeader && shipment.notDeliveredStatusHistories && (
                    <UserNotDeliveredReasons notDeliveredStatusHistories={shipment.notDeliveredStatusHistories} />
                )}
            </Grid>
            <AccordionPhotoPreview
                isOutofStockHeader={!!isOutofStockHeader}
                isExpanded={isExpanded}
                isAnimationRunning={isAnimationRunning}
                items={
                    isOutofStockHeader
                        ? shipment.outofStockItems
                        : shipment.shippingListItemForBackofficeModels
                }
            />
        </Grid>
    );
}

export default UserOrderAccordionHeader;
